import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import IconButton, { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import { useSpeechRecognition } from './useSpeechRecognition';

const VoiceInputButton = observer(({
  onTranscript = () => {},
  onInterimTranscript = () => {},
  onSpeechStateChange = () => {},
  disabled = false
}) => {
  const {
    isListening,
    isSupported,
    error,
    speechState,
    interimText,
    startListening,
    stopListening,
    toggleListening
  } = useSpeechRecognition(onTranscript);

  // Use refs to track previous values and only call callbacks when actually changed
  const prevInterimText = useRef('');
  const prevIsListening = useRef(false);

  // Notify parent about interim text changes (only when actually different)
  useEffect(() => {
    if (interimText !== prevInterimText.current) {
      prevInterimText.current = interimText;
      onInterimTranscript(interimText || '');
    }
  }, [interimText, onInterimTranscript]);

  // Notify parent about listening state changes (only when actually different)
  useEffect(() => {
    if (isListening !== prevIsListening.current) {
      prevIsListening.current = isListening;
      onSpeechStateChange(isListening);
    }
  }, [isListening, onSpeechStateChange]);

  const handleClick = async () => {
    if (!isSupported) {
      alert('Speech recognition is not supported in this browser. Please try Chrome, Safari, or Edge.');
      return;
    }

    // If there's an error and we're not listening, try to start again (allows re-requesting permissions)
    if (error && !isListening) {
      console.log('Attempting to restart speech recognition after error:', error);
      const success = await startListening();
      if (!success) {
        // Show error message for permission issues
        alert(error);
      }
      return;
    }

    console.log('VoiceInputButton clicked, current state:', { isListening, isSupported, error });

    if (isListening) {
      stopListening();
    } else {
      const success = await startListening();
      if (!success && error) {
        // Show permission error
        alert(error);
      }
    }
  };

  const getTooltipText = () => {
    if (!isSupported) {
      return 'Browser is unfortunately incompatible with voice input';
    }

    // Handle permission errors with helpful guidance
    if (error) {
      if (error.includes('denied') || error.includes('NotAllowedError')) {
        return 'Microphone blocked - click to retry and allow permissions';
      }
      if (error.includes('No microphone found')) {
        return 'No microphone detected - check your devices';
      }
      return 'Click to retry voice input';
    }


    if (isListening) {
      return 'Listening... click to stop or pause speaking';
    }

    return 'Click to start voice input';
  };

  return (
    <IconButton
      icon={ICON_BUTTON_ICONS.MICROPHONE}
      onClick={handleClick}
      withFill={false}
      withBorder={false}
      disabled={disabled || !isSupported}
      tooltipText={getTooltipText()}
      tooltipPosition="top"
      wrapClassName={isListening ? "agent-microphone-listening" : ""}
    />
  );
});

export default VoiceInputButton;